import {
  ApplicationRef,
  Component,
  ComponentRef,
  createComponent,
  HostListener,
  Injector,
  Input,
  OnInit,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { SwitcherItemViewModel } from "../view-models/switcher-item.view-model";
import { BroadcastPresetViewModel } from "../view-models/broadcast-preset.view-model";
import { SwitcherViewModel } from "../view-models/switcher.view-model";
import { SwitchItemBlockManagerComponent } from "./switch-item-block-manager/switch-item-block-manager.component";
import {
  assetBlockItemExtendedData,
  AssetGroupEntry,
} from "app/js/src/domain/broadcasts/asset-block-item-extended-data";
import { Subscription } from "rxjs";

const GROUP_COLORS = [
  "#5b9bd5", // slate-blue
  "#e6a23c", // amber
  "#8e6fc9", // purple
  "#d95f6a", // rose
  "#4bacc6", // steel-teal
  "#7b9e5c", // olive-green
];

interface PlaylistItem {
  id: string;
  name: string;
  duration: string;
  isCurrent?: boolean;
}

@Component({
  selector: "evrideo-switcher-item",
  templateUrl: "./switcher-item.component.html",
  styleUrls: ["./switcher-item.component.scss"],
})
export class SwitcherItemComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  vm: SwitcherItemViewModel;

  @Input()
  broadcastPresetViewModel: BroadcastPresetViewModel;

  @Input()
  switcherVm: SwitcherViewModel;

  @Input()
  resizeModeEnabled = false;

  @ViewChild("playlistContainer", { static: false })
  playlistContainer: ElementRef;

  @ViewChild("assetItem", { static: false })
  assetItem: ElementRef;

  playlistItems: PlaylistItem[] = [];
  private popupRef: Window | null = null;
  private compRef: ComponentRef<SwitchItemBlockManagerComponent> | null = null;
  private dataChangeSubscription: Subscription | null = null;
  private popupId: string | null = null;

  // Multi-selection state
  hoveredIndex: number | null = null;
  stackIndex: number = 0;
  stackLength: number = 0;
  animatingIndex: number | null = null;

  // Context menu state
  hoveredRowIndex: number | null = null;
  showContextMenu: boolean = false;
  contextMenuPosition: { x: number; y: number } = { x: 0, y: 0 };
  contextMenuTargetIndex: number | null = null;
  blockScrollToCurrentItem: boolean = false;

  // Drag over state
  isDragOver: boolean = false;

  constructor(
    private appRef: ApplicationRef,
    private _toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.preparePlaylistItems();
    this.subscribeToDataChanges();
  }

  ngAfterViewInit(): void {
    // Initial scroll to current item after view is initialized
    setTimeout(() => {
      this.scrollToCurrentItem();
    }, 0);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["vm"]) {
      this.preparePlaylistItems();
      this.subscribeToDataChanges();
    }
  }

  ngOnDestroy(): void {
    if (this.dataChangeSubscription) {
      this.dataChangeSubscription.unsubscribe();
    }
    // Clean up any open popups when component is destroyed
    this.closePopup();
  }

  private subscribeToDataChanges(): void {
    if (this.dataChangeSubscription) {
      this.dataChangeSubscription.unsubscribe();
    }

    if (this.vm && this.vm.dataChanged$) {
      this.dataChangeSubscription = this.vm.dataChanged$.subscribe(() => {
        this.preparePlaylistItems();
      });
    }
  }

  public preparePlaylistItems() {
    const data = this.vm.data as assetBlockItemExtendedData;
    if (!data.assets) return;
    this.playlistItems = data.assets.map((asset, index) => ({
      id: asset.id,
      name: asset.name,
      duration: this.formatDuration(asset.duration),
      index: index,
    }));
    const extendedData = data.getBroadcastAssetItem(this.vm.uid);
    if (
      extendedData.assetsOrder &&
      Object.entries(extendedData.assetsOrder).length > 0
    ) {
      this.playlistItems.sort((a, b) => {
        return extendedData.assetsOrder[a.id] - extendedData.assetsOrder[b.id];
      });
    }
    if (this.vm.isStack) {
      this.stackIndex = extendedData.stackIndex;
      this.stackLength = extendedData.stackLength;
      this.playlistItems[this.stackIndex].isCurrent = true;

      this.reconcileGroupHeads(extendedData.assetGroups);
    }

    // Scroll to current item after playlist items are updated
    if (this.blockScrollToCurrentItem == false) {
      setTimeout(() => {
        this.scrollToCurrentItem();
      }, 0);
    } else {
      this.blockScrollToCurrentItem = false;
    }
  }

  private reconcileGroupHeads(
    groups: { [assetId: string]: AssetGroupEntry } | undefined,
  ): void {
    if (!groups) return;
    const assetIds = new Set(this.playlistItems.map((item) => item.id));
    const headIds = Object.keys(groups);

    for (const headId of headIds) {
      if (!assetIds.has(headId)) {
        // Head asset was deleted — inherit its group entry to the first asset
        // that comes after it in the current sorted order, if any.
        const entry = groups[headId];
        delete groups[headId];
        // Find the next asset in sorted order that is not itself a group head.
        // We can't rely on index here since the deleted asset is gone; the first
        // item in playlistItems that doesn't already have a group head entry will
        // receive the orphaned entry if no other suitable successor is found.
        if (this.playlistItems.length > 0) {
          const successor = this.playlistItems.find((item) => !groups[item.id]);
          if (successor) {
            groups[successor.id] = entry;
          }
        }
      }
    }
  }

  private scrollToCurrentItem(): void {
    if (
      !this.playlistContainer ||
      !this.playlistItems ||
      this.playlistItems.length === 0
    ) {
      return;
    }

    // Find the current item
    const currentItemIndex = this.playlistItems.findIndex(
      (item) => item.isCurrent,
    );
    if (currentItemIndex === -1) {
      return;
    }

    // Get the playlist container element
    const container = this.playlistContainer.nativeElement;
    if (!container) {
      return;
    }

    // Get all playlist items
    const playlistItems = container.querySelectorAll(".show-list__item");
    if (playlistItems.length === 0 || !playlistItems[currentItemIndex]) {
      return;
    }

    const currentItem = playlistItems[currentItemIndex];

    // Scroll the current item into view
    currentItem.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }

  get isStackEditingDisabled(): boolean {
    return !!this.vm?.isStack && !!this.vm?.isProgram;
  }

  // Multi-selection methods
  onPlayIndicatorHover(index: number): void {
    if (this.vm.isBlock || this.isStackEditingDisabled) {
      return;
    }
    if (index > this.stackIndex) {
      this.hoveredIndex = index;
    }
  }

  onPlayIndicatorLeave(): void {
    this.hoveredIndex = null;
  }

  onPlayIndicatorClick(index: number): void {
    if (
      this.vm.isBlock ||
      this.isStackEditingDisabled ||
      index < this.stackIndex
    ) {
      return;
    }

    // Trigger animation
    this.animatingIndex = index;

    // Reset animation after 300ms
    setTimeout(() => {
      this.animatingIndex = null;
    }, 300);

    this.stackLength = index - this.stackIndex;
    const extendedData = this.vm.data as assetBlockItemExtendedData;
    if (!extendedData.assets) return;
    const ed = extendedData.getBroadcastAssetItem(this.vm.uid);
    ed.stackIndex = this.stackIndex;
    ed.stackLength = this.stackLength;
    this.blockScrollToCurrentItem = true;
    this.vm.updateBlockExtendedData(this.broadcastPresetViewModel.id);
  }

  isInSelectionRange(index: number, fix = 0): boolean {
    if (this.vm.isBlock) {
      return false;
    }

    if (this.hoveredIndex === null || this.hoveredIndex <= this.stackIndex) {
      if (this.stackLength === 0) {
        return false;
      }
      return (
        index >= this.stackIndex &&
        index < this.stackIndex + this.stackLength - fix
      );
    }

    // Don't include the hovered index in the range for the long line
    return index >= this.stackIndex && index < this.hoveredIndex - fix;
  }

  isSelectionEnd(index: number): boolean {
    if (this.vm.isBlock) {
      return false;
    }

    if (this.hoveredIndex !== null) {
      return index == this.hoveredIndex && this.hoveredIndex > this.stackIndex;
    }
    if (this.stackLength === 0) {
      return false;
    }
    return index === this.stackIndex + this.stackLength;
  }

  isAnimating(index: number): boolean {
    return this.animatingIndex === index;
  }

  // Group helpers (read-only, small tile)
  private getGroups(): { [assetId: string]: AssetGroupEntry } {
    const data = this.vm?.data as assetBlockItemExtendedData;
    if (!data) return {};
    const ed = data.getBroadcastAssetItem(this.vm.uid);
    return ed.assetGroups ?? {};
  }

  isGroupHead(index: number): boolean {
    if (!this.vm?.isStack || !this.playlistItems[index]) return false;
    return !!this.getGroups()[this.playlistItems[index].id];
  }

  isInGroup(index: number): boolean {
    if (!this.vm?.isStack) return false;
    const groups = this.getGroups();
    for (let i = index; i >= 0; i--) {
      if (!this.playlistItems[i]) continue;
      const entry = groups[this.playlistItems[i].id];
      if (entry) {
        if (entry.endAssetId) {
          const endIdx = this.playlistItems.findIndex(
            (item) => item.id === entry.endAssetId,
          );
          return endIdx === -1 || index <= endIdx;
        }
        return true;
      }
    }
    return false;
  }

  isGroupEnd(index: number): boolean {
    if (!this.vm?.isStack) return false;
    const currentId = this.playlistItems[index]?.id;
    if (!currentId) return false;
    const groups = this.getGroups();
    for (let i = index; i >= 0; i--) {
      if (!this.playlistItems[i]) continue;
      const entry = groups[this.playlistItems[i].id];
      if (entry) {
        // Explicit end marker
        if (entry.endAssetId === currentId) return true;
        // Implicit end: group has no explicit end and the very next item
        // starts a new group — so this item is the last in its group.
        if (!entry.endAssetId) {
          const nextItem = this.playlistItems[index + 1];
          return !!nextItem && !!groups[nextItem.id];
        }
        return false;
      }
    }
    return false;
  }

  getGroupLabel(index: number): string {
    if (!this.playlistItems[index]) return "";
    const entry = this.getGroups()[this.playlistItems[index].id];
    return entry?.label ?? "";
  }

  getGroupColor(index: number): string {
    const groups = this.getGroups();
    for (let i = index; i >= 0; i--) {
      if (this.playlistItems[i]) {
        const entry = groups[this.playlistItems[i].id];
        if (entry) {
          return GROUP_COLORS[entry.colorIndex % GROUP_COLORS.length];
        }
      }
    }
    return "transparent";
  }

  formatDuration(duration: any): string {
    if (!duration) return "00:00:00.000";

    // If duration is already a string in the correct format, return it
    if (
      typeof duration === "string" &&
      duration.match(/^\d{2}:\d{2}:\d{2}\.\d{3}$/)
    ) {
      return duration;
    }

    // Convert to milliseconds if it's a number
    let milliseconds: number;
    if (typeof duration === "number") {
      milliseconds = duration;
    } else if (typeof duration === "string") {
      // Try to parse as number
      milliseconds = parseFloat(duration) || 0;
    } else {
      milliseconds = 0;
    }

    // Convert to hh:mm:ss.fff format (ceil to next second)
    const totalSeconds = Math.ceil(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const ms = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
  }

  formatDurationToSeconds(durationString: string): string {
    if (!durationString) return "0s";

    // Check if the string matches the expected format hh:mm:ss.fff
    const match = durationString.match(/^(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/);
    if (!match) {
      return "0s";
    }

    const [, hours, minutes, seconds, milliseconds] = match;

    // Convert to total milliseconds
    const totalMs =
      parseInt(hours) * 3600000 +
      parseInt(minutes) * 60000 +
      parseInt(seconds) * 1000 +
      parseInt(milliseconds);

    // Convert to seconds and round to nearest second
    const totalSeconds = Math.round(totalMs / 1000);

    return `${totalSeconds}s`;
  }

  private closeOrphanedPopups() {
    // Check if there are any existing popup windows that might be orphaned
    // This can happen after a page refresh when popupRef is null but popup still exists
    try {
      // Get the stored popup ID from sessionStorage
      const storedPopupId = sessionStorage.getItem(`popup_${this.vm.uid}`);

      if (storedPopupId) {
        try {
          // Try to access the window by the stored ID
          const existingWindow = window.open("", storedPopupId);
          if (existingWindow && existingWindow !== window) {
            // Found the existing popup, close it
            existingWindow.close();
            // Remove from sessionStorage
            sessionStorage.removeItem(`popup_${this.vm.uid}`);
          }
        } catch (e) {
          // Window might not exist or be accessible, remove from storage
          sessionStorage.removeItem(`popup_${this.vm.uid}`);
        }
      }
    } catch (error) {
      // Silently handle any errors in popup detection
      console.warn("Error detecting orphaned popups:", error);
    }
  }

  openBlockManager() {
    if (this.popupRef && !this.popupRef.closed) {
      this.popupRef.focus();
      return;
    }

    // Check for orphaned popups after page refresh
    this.closeOrphanedPopups();
    this.compRef = createComponent(SwitchItemBlockManagerComponent, {
      environmentInjector: this.appRef.injector,
    });
    this.compRef.instance.broadcastUid = this.broadcastPresetViewModel.id;

    this.compRef.instance.vm = this.vm;
    this.compRef.instance.onPreviewClicked.subscribe((event: MouseEvent) => {
      this.broadcastPresetViewModel.onPreviewClicked(event, this.vm);
    });
    this.compRef.instance.onProgramClicked.subscribe((event: MouseEvent) => {
      this.broadcastPresetViewModel.onProgramClicked(event, this.vm);
    });
    this.compRef.instance.changeBlockType.subscribe((event: any) => {
      setTimeout(async () => {
        this.closePopup();
        await this.vm.changeBlockType(this.broadcastPresetViewModel.id);
        this.openBlockManager();
      }, 100);
    });
    const title = `${this.vm.assetName} ${this.vm.isStack ? "Stack" : "Block"} Manager`;
    this.popupId = `popup_${this.vm.uid}_${Date.now()}`;

    // Store the popup ID in sessionStorage so we can find it after page refresh
    sessionStorage.setItem(`popup_${this.vm.uid}`, this.popupId);
    const width = Math.floor(window.innerWidth * 0.7);
    const height = Math.floor(window.innerHeight * 0.5);
    this.popupRef = window.open(
      `/html/popup.html`,
      this.popupId,
      `width=${width},height=${height},left=200,top=200,toolbar=no,menubar=no,scrollbars=no,resizable=no,status=no`,
    );

    // Check if popup was blocked
    if (
      !this.popupRef ||
      this.popupRef.closed ||
      typeof this.popupRef.closed === "undefined"
    ) {
      this.displayPopupBlockedMessage();
      return;
    }

    // Additional check after a short delay to catch popup blockers that don't immediately block
    setTimeout(() => {
      if (!this.popupRef || this.popupRef.closed) {
        this.displayPopupBlockedMessage();
        return;
      }
    }, 100);

    this.popupRef.addEventListener("DOMContentLoaded", () => {
      this.popupRef.document.title = title;

      // Add favicon
      const mainFavicon =
        document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (mainFavicon) {
        const newFavicon = this.popupRef.document.createElement("link");
        newFavicon.rel = "icon";
        newFavicon.href = mainFavicon.href;
        this.popupRef.document.head.appendChild(newFavicon);
      }
      // Create Angular component dynamically

      // Add essential styles to the popup
      this.addStylesToPopup(this.compRef);

      this.appRef.attachView(this.compRef.hostView);
      this.popupRef.document.body.appendChild(
        this.compRef.location.nativeElement,
      );
    });
  }
  private closePopup() {
    if (this.compRef) {
      this.appRef.detachView(this.compRef.hostView);
      this.compRef.destroy();
      this.compRef = null;
    }
    if (this.popupRef) {
      this.popupRef.close();
      this.popupRef = null;
    }
    if (this.popupId) {
      // Remove the popup ID from sessionStorage when closing
      sessionStorage.removeItem(`popup_${this.vm.uid}`);
      this.popupId = null;
    }
  }
  private addStylesToPopup(
    compRef: ComponentRef<SwitchItemBlockManagerComponent>,
  ) {
    if (!this.popupRef) return;

    // Add essential CSS variables and base styles
    const styleElement = this.popupRef.document.createElement("style");
    styleElement.textContent = `
      :root {
        --neutral-2-teal: #55928e;

        --dark-text-clean-white: #ffffff;
        --light-text-primary: linear-gradient(to left, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(to left, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(to left, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.85));
        --success-main: #00ca69;
        --scheduler-text: #000000;
      }

      * {
        box-sizing: border-box;
      }

      body, html {
        margin: 0;
        padding: 0;
        height: 100%;
        font-family: "OpenSans-Regular", sans-serif;
        background-color: #ffffff;
        overflow: hidden;
      }

      /* Essential flexbox utilities */
      .flex {
        display: flex;
      }

      .flex-column {
        flex-direction: column;
      }

      .flex-row {
        flex-direction: row;
      }

      .justify-center {
        justify-content: center;
      }

      .align-center {
        align-items: center;
      }

      .flex-1 {
        flex: 1;
      }
        .loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}
.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 10px;
  font-size: 14px;
  color: #333;
  font-family: "OpenSans-Regular", sans-serif;
}
    `;

    this.popupRef.document.head.appendChild(styleElement);

    // Dynamically load the component's compiled CSS
    setTimeout(() => {
      this.loadStylesFromMainApp();
      compRef.instance.loading = false;
    }, 200);
  }

  private loadStylesFromMainApp() {
    if (!this.popupRef) return;

    // Get all style elements from the main application
    const mainStyles = document.querySelectorAll(
      'style, link[rel="stylesheet"]',
    );

    mainStyles.forEach((styleElement) => {
      if (styleElement.tagName === "STYLE") {
        // Clone the style element
        const clonedStyle = this.popupRef.document.createElement("style");
        clonedStyle.textContent = styleElement.textContent;
        this.popupRef.document.head.appendChild(clonedStyle);
      } else if (styleElement.tagName === "LINK") {
        // Clone the link element for external stylesheets
        const clonedLink = this.popupRef.document.createElement("link");
        clonedLink.rel = "stylesheet";
        clonedLink.href = (styleElement as HTMLLinkElement).href;
        this.popupRef.document.head.appendChild(clonedLink);
      }
    });
  }
  onVisibilityChange(showUid: string) {
    this.blockScrollToCurrentItem = true;
    this.vm.onBlockItemAssetVisibilityChange(
      this.broadcastPresetViewModel.id,
      showUid,
    );
  }

  // Context menu methods
  onRowHover(index: number, event: MouseEvent): void {
    this.hoveredRowIndex = index;
  }

  onRowLeave(index: number, event: MouseEvent): void {
    // Only clear hover state if we're not showing the context menu
    if (!this.showContextMenu) {
      this.hoveredRowIndex = null;
    }
  }

  onThreeDotsClick(index: number, event: MouseEvent): void {
    if (this.isStackEditingDisabled) {
      return;
    }
    event.stopPropagation();
    this.contextMenuTargetIndex = index;

    // Position the context menu near the click
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.contextMenuPosition = {
      x: rect.right - 120,
      y: rect.top + 20,
    };

    this.showContextMenu = true;
  }

  onMovePlayheadClick(): void {
    if (this.isStackEditingDisabled) {
      this.hideContextMenu();
      return;
    }
    if (this.contextMenuTargetIndex !== null && this.vm.isStack) {
      this.stackIndex = this.contextMenuTargetIndex;

      const extendedData = this.vm.data as assetBlockItemExtendedData;
      if (extendedData.assets) {
        const ed = extendedData.getBroadcastAssetItem(this.vm.uid);
        ed.stackIndex = this.stackIndex;
        ed.stackLength = 0;
        this.vm.updateBlockExtendedData(this.broadcastPresetViewModel.id);

        this.preparePlaylistItems();

        this.broadcastPresetViewModel.sendUpdateStackWarmup(
          this.vm.assetId as number,
          this.vm.uid,
        );
      }
    }

    this.hideContextMenu();
  }

  private hideContextMenu(): void {
    this.showContextMenu = false;
    this.contextMenuTargetIndex = null;
    this.hoveredRowIndex = null;
  }

  // Hide context menu when clicking outside
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (this.showContextMenu) {
      this.hideContextMenu();
    }
  }

  private displayPopupBlockedMessage(): void {
    this._toastr.warning(
      `
      <div class="toast-html-container">
      <span>Click the popup blocker icon in your browser's address bar to allow popups</span>
      <br>
      <img src="https://objectstorage.eu-frankfurt-1.oraclecloud.com/n/axcxgqlbvhjy/b/PublicUploads/o/allow-popup.jpg" alt="Allow Popup Instructions" />
      </div>`,
      `${this.vm.assetName} ${this.vm.isStack ? "Stack" : "Block"} Manager Blocked`,
      {
        timeOut: 10000,
        tapToDismiss: true,
        enableHtml: true,
        messageClass: "toast-html",
        positionClass: "toast-top-center",
      },
    );
  }

  private get _resizeAllowed(): boolean {
    return (
      this.resizeModeEnabled &&
      !!this.switcherVm &&
      !!this.vm &&
      this.vm.type !== 0
    );
  }

  get showRightPlus(): boolean {
    return this._resizeAllowed && this.switcherVm.canExpandRight(this.vm);
  }

  get showRightMinus(): boolean {
    return this._resizeAllowed && this.switcherVm.canShrinkRight(this.vm);
  }

  get showBottomPlus(): boolean {
    return this._resizeAllowed && this.switcherVm.canExpandDown(this.vm);
  }

  get showBottomMinus(): boolean {
    return this._resizeAllowed && this.switcherVm.canShrinkDown(this.vm);
  }

  onExpandRight(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.switcherVm || !this.vm) {
      return;
    }
    this.switcherVm.expandRight(this.vm);
    this.broadcastPresetViewModel.updateBroadcastPreset();
  }

  onShrinkRight(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.switcherVm || !this.vm) {
      return;
    }
    this.switcherVm.shrinkRight(this.vm);
    this.broadcastPresetViewModel.updateBroadcastPreset();
  }

  onExpandDown(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.switcherVm || !this.vm) {
      return;
    }
    this.switcherVm.expandDown(this.vm);
    this.broadcastPresetViewModel.updateBroadcastPreset();
  }

  onShrinkDown(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.switcherVm || !this.vm) {
      return;
    }
    this.switcherVm.shrinkDown(this.vm);
    this.broadcastPresetViewModel.updateBroadcastPreset();
  }

  onDragStart(event: DragEvent): void {
    if (!event.dataTransfer || !this.vm || !this.assetItem) {
      return;
    }
    // Mark this as an internal switcher item drag
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "text/switcher-item",
      JSON.stringify({
        uid: this.vm.uid,
        index: this.vm.index,
        type: this.vm.type,
      }),
    );

    // Create a custom drag image from the container
    const containerElement = this.assetItem.nativeElement as HTMLElement;
    if (containerElement) {
      // Add opacity class to the original element first
      containerElement.classList.add("dragging");

      // Create a clone of the container for the drag image
      const dragImage = containerElement.cloneNode(true) as HTMLElement;

      // Style the drag image
      dragImage.style.position = "absolute";
      dragImage.style.top = "-10000px";
      dragImage.style.left = "-10000px";
      dragImage.style.width = `${containerElement.offsetWidth}px`;
      dragImage.style.height = `${containerElement.offsetHeight}px`;
      dragImage.style.opacity = "0.9";
      dragImage.style.transform = "scale(0.85)";
      dragImage.style.transformOrigin = "top left";
      dragImage.style.pointerEvents = "none";
      dragImage.style.zIndex = "10000";
      dragImage.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";

      // Append to body temporarily
      document.body.appendChild(dragImage);

      // Force a reflow to ensure the clone is rendered
      dragImage.offsetHeight;

      // Set the drag image with an offset to position it near the cursor
      const offsetX = (containerElement.offsetWidth * 0.85) / 2;
      const offsetY = (containerElement.offsetHeight * 0.85) / 2;
      event.dataTransfer.setDragImage(dragImage, offsetX, offsetY);

      // Remove the drag image element after a short delay
      setTimeout(() => {
        if (dragImage.parentNode) {
          dragImage.parentNode.removeChild(dragImage);
        }
      }, 0);
    }
  }

  onDragEnd(event: DragEvent): void {
    // Remove the dragging class and opacity
    if (this.assetItem) {
      const containerElement = this.assetItem.nativeElement as HTMLElement;
      if (containerElement) {
        containerElement.classList.remove("dragging");
      }
    }
  }

  onDragEnter(event: DragEvent): void {
    // Only apply to empty items
    if (this.vm && this.vm.type === 0) {
      // Work for any drag type (switcher items, media, blocks, etc.)
      event.preventDefault();
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    // Only apply to empty items
    if (this.vm && this.vm.type === 0) {
      // Check if we're actually leaving the element (not just moving to a child)
      const relatedTarget = event.relatedTarget as HTMLElement;
      const containerElement = this.assetItem?.nativeElement as HTMLElement;

      if (!containerElement || !containerElement.contains(relatedTarget)) {
        this.isDragOver = false;
      }
    }
  }
}
